import { compact, isEqual } from 'lodash'
import { extractKnovEmbedTarget } from './knovLinkTargets'

type EmbeddedAnswer = {
    id?: unknown
}

type AnswerWithEmbeds = {
    embeds?: Array<EmbeddedAnswer | null | undefined>
    [key: string]: unknown
}

/**
 * Remove a trailing URL from read-only rich text when its preview or quote card
 * is expected to carry the link instead. Quill keeps a newline as the final op.
 */
export function cleanTrailingLinks(contents) {
    if (!contents) return contents

    const lastOpIdx = contents?.ops?.length - 1
    const secondToLastIdx = contents?.ops?.length - 2

    return {
        ...contents,
        ops: compact(
            contents?.ops?.reduce((acc, cur, idx) => {
                const link =
                    idx === secondToLastIdx
                        ? contents?.ops?.[secondToLastIdx]?.attributes?.link
                        : null
                return [
                    ...acc,
                    link &&
                    !link.startsWith('@v2quest_') &&
                    (contents?.ops?.[lastOpIdx]?.insert || '')?.trim() === ''
                        ? null
                        : cur,
                ]
            }, []),
        ),
    }
}

function hasPrivateEmbedPlaceholder(answer?: AnswerWithEmbeds | null): boolean {
    return !!answer?.embeds?.some(embed => embed?.id === '__PRIVATE__')
}

function hasVisibleDeltaContent(contents): boolean {
    return !!contents?.ops?.some(op => {
        const insert = op?.insert
        return typeof insert === 'string' ? insert.trim().length > 0 : insert != null
    })
}

function trailingLinkTargetsKnovEmbed(contents): boolean {
    const secondToLastOp = contents?.ops?.[contents?.ops?.length - 2]
    const link = secondToLastOp?.attributes?.link
    return typeof link === 'string' && !!extractKnovEmbedTarget(link)
}

/**
 * Keep the URL visible while a realtime-safe payload can only expose a private
 * embed placeholder. Otherwise the URL and the filtered placeholder both
 * disappear, leaving an apparently blank message until viewer hydration wins.
 */
export function getReadOnlyShowableValue(
    contents,
    {
        answer = null,
        isEmbed = false,
    }: { answer?: AnswerWithEmbeds | null; isEmbed?: boolean } = {},
) {
    const cleaned = cleanTrailingLinks(contents)
    const privatePlaceholderWouldLeaveBlankBody =
        hasPrivateEmbedPlaceholder(answer) &&
        !hasVisibleDeltaContent(cleaned) &&
        trailingLinkTargetsKnovEmbed(contents)
    const shouldHide =
        !isEqual(contents, cleaned) && !isEmbed && !privatePlaceholderWouldLeaveBlankBody

    return shouldHide ? cleaned : contents
}
